import { Clause, Para } from './weeklyRules';

/**
 * Week 6 — post-contest "Free Play" Terms and Conditions (GHS5-85).
 *
 * VERBATIM 1:1 transcription of the executed document
 *   "JNS Free Play Official Rules_IMT Edits 8.13.26 18+.pdf"
 * (delivered 2026-08-13). It supersedes the in-house redline that shipped on
 * 2026-07-23 as `freeplay-2026-v1`. Do not edit, paraphrase, re-order,
 * "improve", or derive the legal text or dates. Verified by whitespace- and
 * typography-normalized word-diff against `pdftotext -layout` output — see
 * web/scripts/verify-rules.tsx.
 *
 * WHAT THE EXECUTED DOC CHANGED vs. the superseded draft (six places):
 *   - "Free Play Conditions" → "Free Play Terms and Conditions" everywhere
 *     (title block, §6, §7, §8, §9, §12, §13).
 *   - Summary block: "until Sponsor ends it" → "until Infinity Marketing
 *     Team, LLC ends it".
 *   - §1: game named "…Free Play Game"; adds the "Free Play Entry" definition
 *     (an Entry is the creation of an account, once, not per session).
 *   - §3: SHORTENED — see the TRANSCRIPTION NOTE on that clause.
 *   - §8: drops the comma after "Terms and Conditions".
 *   - §11: drops "lost, late, or misdirected entries, or"; "not capable of
 *     completion as planned" → "not capable of operation".
 *
 * TWO DEFECTS IN THE SOURCE DOCUMENT, both flagged to legal (2026-08-24):
 *   1. §3 is truncated mid-sentence — it ends "…at the time of entry. O",
 *      the orphaned first letter of the "Officers, directors, employees…"
 *      sentence that defines "Free Play Entities" (and "Household Members").
 *      That definition is therefore MISSING, while the term is relied on in
 *      the ARBITRATION NOTICE ("ANY FREE PLAY ENTITY (AS DEFINED HEREIN)")
 *      and in §4, §7, §9, §10 and §11. Transcribed as executed, minus the
 *      orphan letter — we do not re-add text legal did not execute.
 *   2. The summary block prints "ends it inits sole discretion" (missing
 *      space). Rendered here as "in its" — a spacing artifact, not a wording
 *      change.
 *
 * If legal reissues to fix either: re-verify the body wholesale by word-diff
 * and bump the weekly_rules.version for week 6. Ship the web deploy BEFORE the
 * version bump — the body lives in code and the version lives in the DB, so
 * bumping first would record consent against text the user could not yet have
 * read.
 */

export function FreePlayRules() {
  return (
    <div className="flex w-full flex-col gap-[18px]">
      {/* Document heading, centered above the summary block in the executed
          document (which prints the same line again in the title block below). */}
      <p className="text-center font-['Orbitron',sans-serif] text-[clamp(14px,3.8vw,18px)] font-bold uppercase leading-[1.3] tracking-[0.04em] text-white">
        Free Play Terms and Conditions
      </p>

      {/* Short-form summary block */}
      <Para>
        State Farm&reg; Gamerhood Jake&rsquo;s Neighborhood Swarm Free Play
        (&ldquo;Free Play&rdquo;) is a prize-free, for-fun continuation of the
        Jake&rsquo;s Neighborhood Swarm game that begins September 3, 2026 and
        continues until Infinity Marketing Team, LLC ends it in its sole
        discretion. NO PRIZES ARE AWARDED IN FREE PLAY. NO PURCHASE OR PAYMENT
        OF ANY KIND IS NECESSARY. Void where prohibited or restricted by law.
        Open to legal residents of the United States who are of majority age in
        their state of residence (19+ in AL, DE and NE and 21+ in MS). This is a
        promotion by Infinity Marketing Team, LLC. Twitch does not sponsor or
        endorse this promotion and is not responsible for it. YouTube does not
        sponsor or endorse this promotion and is not responsible for it.
        Secretlabs does not sponsor or endorse this promotion and is not
        responsible for it. Visit [https://statefarmgamerhood.com/rules].
      </Para>

      {/* Title block */}
      <div className="mt-[6px] text-center">
        <p className="font-['Orbitron',sans-serif] text-[clamp(14px,3.8vw,18px)] font-bold uppercase leading-[1.3] tracking-[0.04em] text-white">
          State Farm&reg; Gamerhood Jake&rsquo;s Neighborhood Swarm
        </p>
        <p className="font-['Orbitron',sans-serif] text-[clamp(13px,3.4vw,16px)] font-bold uppercase leading-[1.3] tracking-[0.04em] text-white">
          Free Play Terms and Conditions
        </p>
      </div>

      <Para className="font-bold">
        NO PURCHASE OR PAYMENT OF ANY KIND IS NECESSARY. NO PRIZES ARE AWARDED IN
        FREE PLAY. FREE PLAY IS ONLY OPEN TO LEGAL RESIDENTS OF THE UNITED STATES
        WHO ARE OF MAJORITY AGE IN THEIR STATE OF RESIDENCE (19+ IN AL, DE AND NE
        AND 21+ IN MS). DO NOT PARTICIPATE IF YOU DO NOT RESIDE IN THE UNITED
        STATES OR THE DISTRICT OF COLUMBIA AT THE TIME OF ENTRY AND ARE NOT OF
        MAJORITY AGE. VOID WHERE PROHIBITED OR RESTRICTED BY LAW. SUBJECT TO ALL
        APPLICABLE FEDERAL, STATE AND LOCAL LAWS AND REGULATIONS.
      </Para>

      <Para className="font-bold">
        ARBITRATION NOTICE: TO THE EXTENT PERMITTED BY LAW AND EXCLUDING ANY
        PARTICIPANTS RESIDING OR WORKING WITHIN THE STATES OF CALIFORNIA, NEW
        JERSEY OR NEW YORK, YOU AGREE THAT BY PARTICIPATING IN FREE PLAY, YOU
        AGREE THAT DISPUTES BETWEEN YOU AND ANY FREE PLAY ENTITY (AS DEFINED
        HEREIN) WILL BE RESOLVED BY BINDING, INDIVIDUAL ARBITRATION AND YOU WAIVE
        YOUR RIGHT TO PARTICIPATE IN A JURY TRIAL, CLASS ACTION LAWSUIT OR
        CLASS-WIDE ARBITRATION. SEE DISPUTES/ARBITRATION PROVISION CONTAINED
        HEREIN.
      </Para>

      <ol className="flex flex-col gap-[14px]">
        <Clause n={1} label="GENERAL:">
          State Farm Gamerhood Jake&rsquo;s Neighborhood Swarm Free Play Game
          (&ldquo;Free Play&rdquo;) is administered and sponsored by Infinity
          Marketing Team, LLC (&ldquo;Sponsor&rdquo;). Free Play is subject to
          these Free Play Terms and Conditions and to all applicable federal,
          state, and local laws. Free Play is offered in connection with State
          Farm Mutual Automobile Insurance Company (&ldquo;State Farm&rdquo;),
          One State Farm Plaza, Bloomington, Illinois 61710. For purposes of
          these Terms and Conditions, a &ldquo;Free Play Entry&rdquo; means the
          creation of a user account. Creation of an account is required in order
          to play Free Play; however, an account may be created without playing.
          A Free Play Entry occurs once upon creation of an account and does not
          occur again for any subsequent gameplay session, regardless of the
          number of times a participant plays Free Play using that account. No
          prizes are awarded in Free Play.
        </Clause>

        <Clause n={2} label="FREE PLAY PERIOD:">
          Free Play begins at 12:01 am Eastern Time (&ldquo;ET&rdquo;) on
          September 3, 2026, following the conclusion of the State Farm Gamerhood
          Season 5 Contest, and continues until Sponsor ends it in its sole
          discretion (the &ldquo;Free Play Period&rdquo;). Sponsor may modify,
          suspend, or end Free Play at any time and for any reason without prior
          notice. Eligible participants (&ldquo;participant&rdquo;,
          &ldquo;you&rdquo; or &ldquo;your&rdquo;) may register and play at any
          time during the Free Play Period as set forth in Section 4. Sponsor&rsquo;s
          computer is the official time- and score-keeping device for Free Play.
        </Clause>

        {/* TRANSCRIPTION NOTE — the executed document ends this clause
            mid-sentence: "…at the time of entry. O". The orphaned "O" begins
            the "Officers, directors, employees…" sentence that defines "Free
            Play Entities" and "Household Members"; that definition, and the
            "Void where prohibited… / solely responsible for all costs"
            closing that followed it in the superseded draft, are absent from
            the executed text. Transcribed as executed, minus the orphan
            letter. Do not restore the missing sentences without a reissued
            document. Flagged to legal 2026-08-24. */}
        <Clause n={3} label="ELIGIBILITY:">
          Free Play is open only to legal residents of the fifty (50) United
          States and the District of Columbia, who, as of the start of
          participation, are the age of majority or older in their state of
          residence (19+ in AL, DE and NE, 21+ in MS, and 18+ in all other
          states). Do not participate if you are not located in the United States
          or District of Columbia and are not the age of majority at the time of
          entry.
        </Clause>

        <Clause n={4} label="HOW TO PLAY:">
          During the Free Play Period, you may participate on any
          internet-connected device by visiting https://statefarmgamerhood.com
          (the &ldquo;Website&rdquo;) and filling out the on-screen registration
          form. Normal internet access or device usage charges imposed by mobile
          carriers may apply, which shall be your sole responsibility. One account
          per person. The authorized account holder of the email account provided
          during registration will be deemed to be the participant. The Free Play
          Entities are not liable for any failure of registration, regardless of
          cause or the person responsible, including, but not limited to, any
          problem or technical malfunction of any computer, online system, or
          server or technical problem. Registered participants who use
          inappropriate or offensive usernames (as determined by Sponsor in its
          sole discretion) will be disqualified from participation. Sponsor will
          determine in its sole discretion all eligibility requirements in
          connection with Free Play.
        </Clause>

        <Clause n={5} label="GAME PLAY:">
          During the Free Play Period, participants may play (an unlimited number
          of times) a game called Jake&rsquo;s Neighborhood Swarm, a platform game
          where you will, as Jake the player character, earn points by collecting
          coins and hearts, eliminating enemies, and defeating bosses, all while
          avoiding losing health from enemy strikes. As game play proceeds, Jake
          will advance through levels of increasing difficulty (e.g., faster
          enemies and more enemies to avoid). The object of the game is to acquire
          as many points as possible before Jake loses all of his health from
          enemy strikes. Game play and point accumulation will occur as follows:
          <Para className="mt-[12px]">
            Jake begins game play with four hearts, signifying full health. Each
            time Jake is struck by an enemy, he loses one heart. However, Jake can
            earn extra hearts during game play, restoring his health. Once Jake
            loses all four hearts, game play is over. At the conclusion of each
            game, you will receive a score based on a combination of: (i) coins
            collected (1 point each), (ii) hearts collected (1 point each), (iii)
            bosses defeated (1000 points each); (iv) a &ldquo;life bonus&rdquo;
            (the number of hearts you have when defeating a boss x 500); and (v)
            levels achieved (the number of levels you reach x 30). Your score is
            recorded into the database and may be shown on a free-play leaderboard
            for informational and entertainment purposes only. Free Play scores do
            not earn any prize, award, or other consideration.
          </Para>
        </Clause>

        <Clause n={6} label="CONDUCT:">
          Sponsor reserves the right in its discretion to disqualify any
          participant who: (i) tampers or attempts to tamper with the operation of
          Free Play, including the entry process; (ii) violates these Free Play
          Terms and Conditions; or (iii) acts in an unsportsmanlike or disruptive
          manner, or with intent to annoy, abuse, threaten or harass any other
          person. In the event a participant attempts to deliberately damage any
          associated website or platform, undermine the operation of Free Play, or
          otherwise violate any applicable laws in connection with such
          participant&rsquo;s participation, Sponsor reserves the right to pursue
          all legal remedies available to it against such participant. Use of
          bots, computer scripts or programs and other automatic methods of game
          play are prohibited.
        </Clause>

        <Clause n={7} label="LIABILITY:">
          Free Play Entities are not responsible for any incorrect or inaccurate
          information, whether caused by users, human error, tampering, hacking or
          by any of the equipment or programming associated with or utilized in
          Free Play and assume no responsibility for any error, omission,
          interruption, deletion, defect, delay in operation or transmission
          (including availability or accessibility of the internet),
          incompatibility, communications failure, theft, loss or destruction of
          entries, nor for the failure to capture an entry or other information.
          Free Play Entities are not responsible for injury or damage to
          participants&rsquo; or to any other person&rsquo;s phone, tablet,
          computer or other device related to or resulting from downloading
          materials from or use of the Website or otherwise as a result of your
          participation in Free Play. If, for any reason, Free Play or any element
          thereof is not capable of running as planned by reason of, but not
          limited to, tampering, unauthorized intervention, fraud, force majeure,
          technical or other failures or errors, or any other causes similar or
          dissimilar which Sponsor deems, in its opinion, could corrupt or affect
          the administration, security, fairness, integrity or proper conduct of
          Free Play or any element thereof, Free Play Entities reserve the right at
          their discretion to cancel, terminate, modify or suspend Free Play or any
          element thereof. In the event of any discrepancy, ambiguity,
          inconsistency, printing or any other error or miscommunication in any
          communications, announcements, advertising or promotional materials
          relating to Free Play, these Free Play Terms and Conditions shall govern.
        </Clause>

        <Clause n={8} label="PRIVACY:">
          All information submitted by participants is subject to these Free Play
          Terms and Conditions and will be treated in a manner consistent with
          Sponsor&rsquo;s Privacy Policy and State Farm&rsquo;s Terms of Use and
          Privacy Policy. California residents only, please see: Do Not Sell or
          Share My Personal Information. By participating in Free Play,
          participants hereby agree that Sponsor and/or State Farm may collect and
          use their personal information in compliance with their respective
          Privacy Policies and acknowledge that they have read and accepted each
          such policy. By participating in Free Play, participants further agree
          and acknowledge that Sponsor and/or State Farm may share their personal
          information with third parties in order for the third party to market
          State Farm&rsquo;s products and services on behalf of State Farm,
          consistent with State Farm&rsquo;s Privacy Policy.
        </Clause>

        <Clause n={9} label="INDEMNIFICATION AND RELEASE OF LIABILITY:">
          BY PARTICIPATING, YOU AGREE TO THE FULLEST EXTENT PERMITTED BY LAW TO
          RELEASE, DISCHARGE AND HOLD HARMLESS TWITCH, YOUTUBE, SECRETLABS, THE
          FREE PLAY ENTITIES AND THEIR RESPECTIVE PARENT, AFFILIATES AND SUBSIDIARY
          COMPANIES, ADVERTISING AND PROMOTION AGENCIES, AND THEIR RESPECTIVE
          OFFICERS, MANAGERS, DIRECTORS, EMPLOYEES, INDEPENDENT CONTRACTORS,
          REPRESENTATIVES AND AGENTS (&ldquo;RELEASED PARTIES&rdquo;) FROM AND
          AGAINST ANY AND ALL ALLEGED OR ACTUAL CLAIMS, CAUSES OF ACTION, DEMANDS,
          LOSSES, SETTLEMENTS, LIABILITIES AND DAMAGES OF ANY KIND WHATSOEVER
          EXISTING NOW OR ARISING IN THE FUTURE (INCLUDING, WITHOUT LIMITATION,
          BODILY INJURY, PERSONAL INJURY, DEATH, DISABILITY AND PROPERTY DAMAGE,
          VIOLATION OF PROPRIETARY, PUBLICITY, PRIVACY OR ANY OTHER RIGHT), COSTS
          AND EXPENSES (INCLUDING, WITHOUT LIMITATION, REASONABLE ATTORNEYS&rsquo;
          FEES, COURT COSTS, SETTLEMENT AND DISBURSEMENTS) DIRECTLY OR INDIRECTLY
          ARISING OUT OF YOUR PARTICIPATION IN FREE PLAY IN WHOLE OR IN PART. By
          participating in Free Play, you covenant to the fullest extent permitted
          by law not to sue any Released Party or cause them to be sued regarding
          any matter released above, and not to disaffirm, limit or rescind this
          release. A waiver by one or more of the Free Play Entities of any term in
          these Free Play Terms and Conditions does not constitute a waiver of any
          other provision. SOME JURISDICTIONS DO NOT ALLOW CERTAIN LIMITATIONS OR
          EXCLUSION OF LIABILITY, SO THE FOREGOING MAY NOT APPLY TO EVERY
          PARTICIPANT. Any provision adjudged to be invalid shall be struck from
          these Free Play Terms and Conditions and the remainder shall continue in
          full force and effect.
        </Clause>

        <Clause n={10} label="FORCE MAJEURE:">
          Free Play Entities shall not be liable to participants or any other
          person or entity for failure to execute Free Play by reason of any act of
          God, any action, regulation, order or request by any governmental or
          quasi-governmental entity (whether or not the action, regulation, order
          or request proves to be invalid), equipment failure, terrorist act,
          cyber-attack, earthquake, war, fire, flood, explosion, unusually severe
          weather, hurricane, epidemics, pandemics, embargo, labor dispute or
          strike (whether legal or illegal), labor or material shortage,
          transportation interruption of any kind, work slow-down, civil
          disturbance, insurrection, riot, cancellation or delay of any event, or
          any similar or dissimilar event beyond their reasonable control.
        </Clause>

        <Clause n={11} label="DISCLAIMERS:">
          Free Play Entities are not responsible for telecommunication, internet,
          or hardware or software failures, including by reason of any bug or
          computer virus or other failure. Free Play Entities may cancel, modify,
          or terminate Free Play (including the game) without prior notice if it is
          not capable of operation, including by reason of infection of computer
          virus, tampering, unauthorized intervention, force majeure, or technical
          difficulties of any kind.
        </Clause>

        <Clause n={12} label="ALTERNATIVE DISPUTE RESOLUTION; ARBITRATION:">
          To the extent permitted by law and excluding any participants residing
          or working within the States of California, New Jersey or New York, any
          disputes relating to the terms of these Free Play Terms and Conditions or
          the breach thereof, or connected with Free Play, shall be submitted to
          binding arbitration in Los Angeles, CA in accordance with the Commercial
          Arbitration rules of the American Arbitration Association (the
          &ldquo;AAA&rdquo;). Each party agrees that any dispute resolution
          proceedings will be conducted only on an individual basis and not in a
          class, consolidated or representative action. Each party hereby waives
          the right to participate in any class action, whether in court or
          arbitration. In the event that either party desires to arbitrate any
          such dispute, such party shall so notify the other party and the parties
          shall endeavor in good faith, for a period of 30 days, to resolve such
          dispute without arbitration. In the event that the parties cannot resolve
          the dispute within such 30 day period, then within 10 days thereafter,
          the parties shall jointly designate a single arbitrator to hear the
          dispute, or, if the parties are unable to jointly select an arbitrator,
          an arbitrator shall be chosen according to the rules of the AAA. The
          arbitration, including the rendering of the award, shall take place in
          Los Angeles, CA and shall be the exclusive forum for resolving such
          dispute, controversy, or claim. The decision of the arbitrator shall be
          final and binding upon the parties hereto. The decision of the arbitrator
          shall be executory, and judgment thereon may be entered by any court of
          competent jurisdiction. If for any reason a claim proceeds in court
          rather than in arbitration, each party waives any right to a jury trial,
          excluding in any civil matters brought within the States of California,
          New Jersey, or New York.
        </Clause>

        <Clause n={13} label="SEVERABILITY:">
          The invalidity, illegality, or unenforceability of any provision of these
          Free Play Terms and Conditions shall in no way affect the validity,
          legality, or enforceability of any other provision hereof. Any invalid,
          illegal, or unenforceable provision shall be deemed severed from these
          Free Play Terms and Conditions and the balance of these Free Play Terms
          and Conditions shall be construed and enforced as if these Free Play
          Terms and Conditions did not contain such invalid, illegal, or
          unenforceable provision.
        </Clause>
      </ol>

      <Para className="mt-[6px]">
        &copy; 2026 Infinity Marketing Team, LLC. All rights reserved.
      </Para>
    </div>
  );
}
